<template>
  <div class="template">
    <div class="block">
      <form class="flex flex-col gap-6 w-full" @submit.prevent>
        <div
          class="w-full overflow-hidden rounded-[var(--radius)] border border-border"
        >
          <div
            class="bg-muted/30 px-5 py-3 border-b border-border flex items-center justify-between gap-3"
          >
            <div class="text-sm font-medium text-foreground">
              {{ $t("Оплата") }}
            </div>
          </div>
          <div class="px-5 py-3">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
              <FormItem float>
                <FormLabel>{{ $t("Гос. пошлина") }}</FormLabel>
                <FormControl>
                  <Input v-model="form.state_duty" class="w-full" />
                </FormControl>
              </FormItem>

              <FormItem float>
                <FormLabel>{{ $t("Сумма договора") }}</FormLabel>
                <FormControl>
                  <Input v-model="form.contract_amount" class="w-full" />
                </FormControl>
              </FormItem>
            </div>
          </div>
        </div>

        <template v-if="templateFieldList.length">
          <SectionBlock :title="$t('Дополнительные данные')">
            <div
              v-for="item in templateFieldList"
              :key="item.key"
              class="flex flex-col gap-2"
            >
              <div v-if="item.type !== 'bool'" class="flex flex-col gap-1.5">
                <label class="text-sm font-medium leading-none">
                  {{ item.name }}
                </label>
                <component
                  :is="getFieldComponent(item)"
                  :item="item"
                  :form="form"
                  @update="updateField"
                />
              </div>
              <component
                v-else
                :is="getFieldComponent(item)"
                :item="item"
                :form="form"
                @update="updateField"
              >
                <template #action>
                  <template v-if="existingRelatedKeys(item).length">
                    <FormField
                      v-for="relKey in existingRelatedKeys(item)"
                      :key="relKey"
                      :name="relKey"
                    >
                      <FormItem float class="w-full">
                        <FormLabel>{{
                          $t(getRelatedField(relKey).name)
                        }}</FormLabel>
                        <FormControl>
                          <Input
                            :model-value="
                              form.template_fields[relKey].value || ''
                            "
                            class="w-full"
                            @update:model-value="onRelatedInput(relKey, $event)"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    </FormField>
                  </template>

                  <div
                    v-else-if="missingRelatedKeys(item).length"
                    class="text-xs text-muted-foreground mt-1"
                  >
                    {{ $t("Связанное поле не добавлено в шаблон") }}:
                    {{ missingRelatedKeys(item).join(", ") }}
                  </div>
                </template>
              </component>
            </div>
          </SectionBlock>
        </template>
      </form>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed, watch } from "vue";
import { useForm } from "vee-validate";
import SectionBlock from "@/shared/ui/section-block/SectionBlock.vue";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import FieldBoolean from "@/features/dynamic-fields/ui/FieldBoolean.vue";
import FieldString from "@/features/dynamic-fields/ui/FieldString.vue";
import FieldDecimal from "@/features/dynamic-fields/ui/FieldDecimal.vue";
import FieldInt from "@/features/dynamic-fields/ui/FieldInt.vue";
import FieldArray from "@/features/dynamic-fields/ui/FieldArray.vue";
import FieldDate from "@/features/dynamic-fields/ui/FieldDate.vue";
import FieldJson from "@/features/dynamic-fields/ui/FieldJson.vue";
import FieldEnum from "@/features/dynamic-fields/ui/FieldEnum.vue";

const props = defineProps({
  form: { type: Object, required: true },
});

const { setFieldValue, setFieldError } = useForm();

const fieldRelations = reactive({
  issued_for_abroad: ["country_names"],
  is_outside_office: ["address_transaction"],
  is_for_foreign_citizens: ["name_of_foreign_citizens"],
});

const templateFieldList = computed(() => {
  if (!props.form?.template_fields) return [];

  const relatedKeys = new Set(Object.values(fieldRelations).flat());
  const priorityOrder = [
    "issued_for_abroad",
    "is_outside_office",
    "is_for_foreign_citizens",
  ];

  return Object.entries(props.form.template_fields)
    .map(([key, field]) => ({ key, ...field }))
    .filter((item) => !relatedKeys.has(item.key))
    .sort((a, b) => {
      const aPriority = priorityOrder.indexOf(a.key);
      const bPriority = priorityOrder.indexOf(b.key);

      if (aPriority !== -1 || bPriority !== -1) {
        if (aPriority === -1) return 1;
        if (bPriority === -1) return -1;
        return aPriority - bPriority;
      }
      if (a.type === "bool" && b.type !== "bool") return -1;
      if (a.type !== "bool" && b.type === "bool") return 1;
      return 0;
    });
});

const activeRelatedKeys = computed(() => {
  const keys = [];
  for (const item of templateFieldList.value) {
    if (item.type !== "bool") continue;
    const val = props.form.template_fields?.[item.key]?.value;
    if (!val) continue;
    const related = fieldRelations[item.key] || [];
    for (const rk of related) {
      if (props.form.template_fields?.[rk]) {
        keys.push(rk);
      }
    }
  }
  return keys;
});

watch(
  () => props.form.template_fields,
  (fields) => {
    if (!fields) return;
    const allRelated = Object.values(fieldRelations).flat();
    for (const key of allRelated) {
      if (fields[key]?.value) {
        setFieldValue(key, fields[key].value);
      }
    }
  },
  { deep: true, immediate: true }
);

function getFieldComponent(item) {
  if (item.enums?.length) return FieldEnum;

  const map = {
    bool: FieldBoolean,
    string: FieldString,
    decimal: FieldDecimal,
    int: FieldInt,
    ARRAY: FieldArray,
    DateTime: FieldDate,
    json: FieldJson,
  };

  return map[item.type] || FieldString;
}

function updateField(key, value) {
  if (props.form?.template_fields?.[key]) {
    props.form.template_fields[key].value = value ? value : null;
    return;
  }
  props.form[key] = value;
}

function onRelatedInput(relKey, val) {
  props.form.template_fields[relKey].value = val;
  setFieldValue(relKey, val);
}

function existingRelatedKeys(item) {
  return getRelatedKeys(item).filter((k) => !!getRelatedField(k));
}

function getRelatedField(key) {
  return props.form.template_fields?.[key];
}

function getRelatedKeys(item) {
  return fieldRelations[item.key] || [];
}

function missingRelatedKeys(item) {
  return getRelatedKeys(item).filter((k) => !getRelatedField(k));
}

async function validate() {
  let valid = true;

  for (const relKey of activeRelatedKeys.value) {
    const val = props.form.template_fields?.[relKey]?.value;
    if (!val || !val.toString().trim()) {
      setFieldError(relKey, " ");
      valid = false;
    }
  }

  return valid;
}

async function submit() {
  const valid = await validate();
  if (!valid) return null;

  return buildPutPayload();
}

function buildPutPayload() {
  const payload = {};

  Object.entries(props.form).forEach(([key, value]) => {
    if (key !== "template_fields" && value !== undefined) {
      payload[key] = value;
    }
  });

  Object.entries(props.form.template_fields || {}).forEach(([key, field]) => {
    if (field.value !== undefined) {
      payload[key] = field.value;
    }
  });

  return payload;
}

defineExpose({ submit, validate });
</script>

<style lang="scss" scoped>
.template {
  @apply flex h-full w-full overflow-x-hidden gap-5;
  .block {
    @apply h-full overflow-auto rounded-2xl p-8 w-1/2 bg-background;

    @media (max-width: 1400px) {
      @apply w-full;
    }
  }
}
</style>
